import React, { useState, useRef } from 'react';
import { pbAuthedFetch } from "../../authHeaders";
import { useQuery, useMutation } from '@tanstack/react-query';
import IngestPipeline from './IngestPipeline';
import { Icon } from './icons';

// Data sources: connections, bindings, editable data tables, and ingest pipeline health.

const SECTIONS = [
  { id: 'connections', label: 'Connections & Tables' },
  { id: 'pipeline', label: 'Ingest Pipeline' },
] as const;

type SectionId = typeof SECTIONS[number]['id'];

interface Connection {
  name: string;
  enabled: boolean;
  host: string | null;
  configUpdatedAt: string | null;
}

interface Binding {
  id: number;
  connector: string;
  projectName: string;
  scope: string;
  fieldMapState: string;
  testedState: string;
  testedAt: string | null;
  baselineRef: string | null;
  note: string | null;
}

const MAP_TONE: Record<string, string> = { ok: 'ok', partial: 'warn', unmapped: 'risk', untested: 'steel' };
const TEST_TONE: Record<string, string> = { pass: 'ok', warn: 'warn', fail: 'risk', untested: 'steel' };

// Grouped the same way Connectors groups its cards (settings.css's shared
// .stg-picker-* rules) — 16 flat items in one row was the same
// hard-to-find problem Connectors had with 8.
const TABLE_GROUPS = [
  { id: 'delivery', label: 'Delivery' },
  { id: 'financials', label: 'Financials' },
  { id: 'oversight', label: 'Oversight' },
  { id: 'outcomes', label: 'Outcomes' },
];

interface TableMeta { key: string; label: string; group: string }

const EDITABLE_TABLES: TableMeta[] = [
  { key: 'contracts', label: 'Contracts', group: 'delivery' },
  { key: 'schedule_baselines', label: 'Schedule baselines', group: 'delivery' },
  { key: 'sla_measurements', label: 'Availability measurements', group: 'delivery' },
  { key: 'gl_balances', label: 'General-ledger balances', group: 'financials' },
  { key: 'cost_allocations', label: 'Cost allocations', group: 'financials' },
  { key: 'cloud_spend', label: 'Cloud spend', group: 'financials' },
  { key: 'cloud_waste', label: 'Cloud savings', group: 'financials' },
  { key: 'unit_metrics', label: 'Unit economics', group: 'financials' },
  { key: 'escalations', label: 'Escalations', group: 'oversight' },
  { key: 'decisions', label: 'Decisions', group: 'oversight' },
  { key: 'mandates', label: 'Mandates', group: 'oversight' },
  { key: 'findings', label: 'IG and GAO findings', group: 'oversight' },
  { key: 'mission_outcomes', label: 'Mission outcomes', group: 'outcomes' },
  { key: 'indicators', label: 'Leading indicators', group: 'outcomes' },
  { key: 'forecasts', label: 'Forecasts', group: 'outcomes' },
  { key: 'attribution', label: 'Blocker attribution', group: 'outcomes' },
];

const EDITABLE = EDITABLE_TABLES.map((t) => t.key);
const RESOURCE_LABELS: Record<string, string> = Object.fromEntries(EDITABLE_TABLES.map((t) => [t.key, t.label]));

interface DataSourcesProps {
  isAdmin: boolean;
}

function initialSection(): SectionId {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('section');
    return SECTIONS.some((s) => s.id === fromUrl) ? (fromUrl as SectionId) : 'connections';
  } catch {
    return 'connections';
  }
}

export default function DataSources({ isAdmin }: DataSourcesProps) {
  const [section, setSection] = useState<SectionId>(initialSection);
  const [testing, setTesting] = useState<number | null>(null);
  const [testErrors, setTestErrors] = useState<Record<number, string>>({});
  const [testedOk, setTestedOk] = useState<Record<number, boolean>>({});
  const [activeResource, setActiveResource] = useState(EDITABLE[0]);
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const { data: connectionsData, isLoading: connLoading, error: connError, refetch: connRefetch } = useQuery<{ connections: Connection[]; note?: string }>({
    queryKey: ['command-connections'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/command/connections');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
    enabled: isAdmin,
  });

  const { data: bindingsData, isLoading: bindLoading, error: bindError, refetch: bindRefetch } = useQuery<{ bindings: Binding[] }>({
    queryKey: ['command-bindings'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/command/bindings');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
    enabled: isAdmin,
  });

  const testBinding = useMutation({
    mutationFn: async (id: number) => {
      const res = await pbAuthedFetch(`/api/command/bindings/${id}/test`, { method: 'POST' });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || `Request failed (${res.status})`);
      return body;
    },
  });

  function onTabKeyDown(e: React.KeyboardEvent) {
    const i = EDITABLE.indexOf(activeResource);
    let ni = i;
    switch (e.key) {
      case 'ArrowRight': case 'ArrowDown': ni = (i + 1) % EDITABLE.length; break;
      case 'ArrowLeft': case 'ArrowUp': ni = (i - 1 + EDITABLE.length) % EDITABLE.length; break;
      case 'Home': ni = 0; break;
      case 'End': ni = EDITABLE.length - 1; break;
      default: return;
    }
    e.preventDefault();
    const next = EDITABLE[ni];
    setActiveResource(next);
    const el = tabRefs.current[next];
    if (el) el.focus();
  }

  async function runTest(id: number) {
    setTesting(id);
    setTestErrors((prev) => { const { [id]: _, ...rest } = prev; return rest; });
    setTestedOk((prev) => { const { [id]: _, ...rest } = prev; return rest; });
    try {
      await testBinding.mutateAsync(id);
      setTestedOk((prev) => ({ ...prev, [id]: true }));
      bindRefetch();
    } catch (err: any) {
      setTestErrors((prev) => ({ ...prev, [id]: err.message }));
    } finally {
      setTesting(null);
    }
  }

  if (!isAdmin) {
    return (
      <div className="box">
        <div className="box__body">
          <div className="state state--denied">
            <div className="state__title">Admin access required</div>
            <div className="state__body">Connections name hosts and bindings define which slice of a source feeds which program, so both are restricted.</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="tabs" role="tablist" aria-label="Data source sections" aria-orientation="horizontal">
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            type="button"
            role="tab"
            aria-selected={section === s.id}
            tabIndex={section === s.id ? 0 : -1}
            className="tab"
            onClick={() => setSection(s.id)}
          >
            {s.label}
          </button>
        ))}
      </div>

      {section === 'pipeline' ? (
        <IngestPipeline />
      ) : (
      <>
      <div className="box">
        <header className="box__head">
          <div className="box__head-text">
            <p className="box__eyebrow">Data sources · Sources</p>
            <h2 className="box__title">Connections</h2>
          </div>
        </header>
        <div className="box__body">
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-45)', margin: '0 0 var(--sp-4)' }}>
            Each connection is one instance plus its credentials. A single source can serve several programs.
          </p>
          {connLoading ? <div className="state"><div className="state__title">Loading connections…</div></div> : null}
          {connError ? (
            <div className="state state--risk">
              <div className="state__title">Could not load connections</div>
              <div className="state__body">{String(connError)}<br /><button className="btn btn--sm" onClick={() => connRefetch()} style={{ marginTop: 8 }}>Retry</button></div>
            </div>
          ) : null}
          {connectionsData ? (
            <>
              <div className="tbl-scroll">
                <table className="tbl">
                  <caption className="pb-sr-only">Configured source connections</caption>
                  <thead>
                    <tr>
                      <th scope="col">Source</th>
                      <th scope="col">State</th>
                      <th scope="col">Host</th>
                      <th scope="col">Config updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(connectionsData.connections || []).map((c) => (
                      <tr key={c.name}>
                        <td><span className="td--name">{c.name || '—'}</span></td>
                        <td><span className={`pill${c.enabled ? ' pill--ok' : ''}`}>{c.enabled ? 'enabled' : 'disabled'}</span></td>
                        <td>{c.host ? <span className="td--id">{c.host}</span> : '—'}</td>
                        <td>
                          {c.configUpdatedAt ? (
                            <span style={{ fontFamily: 'var(--mono)', fontVariantNumeric: 'tabular-nums' }}>{String(c.configUpdatedAt).slice(0, 10)}</span>
                          ) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {connectionsData.note ? <p className="stg-fig" style={{ marginTop: 8 }}>{connectionsData.note}</p> : null}
            </>
          ) : null}
        </div>
      </div>

      <div className="box">
        <header className="box__head">
          <div className="box__head-text">
            <p className="box__eyebrow">Data sources · Connection → program slice</p>
            <h2 className="box__title">Bindings</h2>
          </div>
        </header>
        <div className="box__body">
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-45)', margin: '0 0 var(--sp-4)' }}>
            A binding maps which slice of a connection feeds which program.
          </p>
          {bindLoading ? <div className="state"><div className="state__title">Loading bindings…</div></div> : null}
          {bindError ? (
            <div className="state state--risk">
              <div className="state__title">Could not load bindings</div>
              <div className="state__body">{String(bindError)}</div>
            </div>
          ) : null}
          {bindingsData ? (
            !bindingsData.bindings || bindingsData.bindings.length === 0 ? (
              <div className="state"><div className="state__title">No bindings configured</div><div className="state__body">Add a binding to map a connection's slice onto a program.</div></div>
            ) : (
              <ul className="stg-binding-list">
                {(bindingsData.bindings || []).map((b) => (
                  <li key={b.id} className="stg-binding-row">
                    <div className="stg-binding-hd">
                      <strong>{b.connector}</strong>
                      <span className="stg-fig">→ {b.projectName}</span>
                      <span className="pill">{b.scope}</span>
                      <span className={`pill pill--${MAP_TONE[b.fieldMapState] || ''}`}>{b.fieldMapState}</span>
                      <span className={`pill pill--${TEST_TONE[b.testedState] || ''}`}>test {b.testedState}</span>
                      <button type="button" className="btn btn--sm" aria-label={testing === b.id ? `Testing binding ${b.id}` : `Test binding ${b.id}`} disabled={testing === b.id} onClick={() => runTest(b.id)}>
                        {testing === b.id ? 'Testing…' : 'Test'}
                      </button>
                    </div>
                    <p className="stg-fig">
                      baseline {b.baselineRef || 'not recorded'} · {b.testedAt ? `last tested ${String(b.testedAt).slice(0, 10)}` : 'never tested'}
                    </p>
                    {b.note ? <p className="stg-fig">{b.note}</p> : null}
                    {testErrors[b.id] ? <p className="stg-fig" style={{ color: 'var(--risk)' }} role="status">Test failed: {testErrors[b.id]}</p> : null}
                    {testedOk[b.id] ? <p className="stg-fig" style={{ color: 'var(--ok)' }} role="status" aria-live="polite">Test completed.</p> : null}
                  </li>
                ))}
              </ul>
            )
          ) : null}
        </div>
      </div>

      <div className="stg-picker-shell">
        <nav className="stg-picker-list" role="tablist" aria-label="Editable data tables" aria-orientation="vertical" onKeyDown={onTabKeyDown}>
          {TABLE_GROUPS.map((g) => {
            const items = EDITABLE_TABLES.filter((t) => t.group === g.id);
            if (!items.length) return null;
            return (
              <div className="stg-picker-group" key={g.id}>
                <h3 className="stg-picker-group-label">{g.label}</h3>
                {items.map((t) => {
                  const isActive = t.key === activeResource;
                  return (
                    <button
                      key={t.key}
                      type="button"
                      role="tab"
                      id={`ds-tab-${t.key}`}
                      aria-selected={isActive}
                      aria-controls={isActive ? `ds-panel-${t.key}` : undefined}
                      tabIndex={isActive ? 0 : -1}
                      ref={(el) => { tabRefs.current[t.key] = el; }}
                      className={`stg-picker-item${isActive ? ' is-active' : ''}`}
                      onClick={() => setActiveResource(t.key)}
                    >
                      <Icon name="database" size={15} />
                      <span className="stg-picker-item-label">{t.label}</span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </nav>

        <div role="tabpanel" id={`ds-panel-${activeResource}`} aria-labelledby={`ds-tab-${activeResource}`} className="stg-picker-detail">
          <div className="box">
            <header className="box__head">
              <div className="box__head-text">
                <p className="box__eyebrow">Editable data table</p>
                <h2 className="box__title">{RESOURCE_LABELS[activeResource] || activeResource}</h2>
              </div>
            </header>
            <div className="box__body">
              <div className="state">
                <div className="state__title">Table editor for {RESOURCE_LABELS[activeResource]}</div>
                <div className="state__body">Records for this table can be managed through the API or the record editor interface.</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </>
      )}
    </>
  );
}
